import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Button } from "@/components/ui/button"
import { GradeBadge } from "@/components/ui/grade-badge"
import { FindingCard } from "@/components/ui/finding-card"
import { FindingGroup } from "@/components/ui/finding-group"

type Check = {
  key: string
  label: string
  status: "pass" | "warn" | "fail"
  finding: string
  detail?: string
}

type Results = {
  fetched_at: string
  final_url: string | null
  response_time_ms: number | null
  html_bytes: number | null
  checks: Check[]
}

type Props = {
  token: string
  url: string
  business_name: string | null
  status: "pending" | "running" | "complete" | "failed"
  grade: string | null
  score: number | null
  results: Results | null
  created_at: string
}

const CHECK_NAMES = [
  "Site loads", "HTTPS / SSL", "Spam & hack scan", "Title & description",
  "Mobile-ready", "Page headline", "Local business schema", "Image descriptions",
  "Speed & page weight", "Platform exposure",
]

export default function SeoAuditResult({ url, status, grade, score, results }: Props) {
  const inFlight = status === "pending" || status === "running"

  // Polling gives up after 2 minutes. Giving up has to be visible: silently
  // stopping left the page insisting "Running the audit" forever, which reads
  // as a hang.
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!inFlight) {
      setTimedOut(false) // a late result should clear a previous timeout
      return
    }
    const interval = setInterval(() => {
      router.reload({ only: ["status", "grade", "score", "results"] })
    }, 2500)
    const bail = setTimeout(() => {
      clearInterval(interval)
      setTimedOut(true)
    }, 120_000)
    return () => {
      clearInterval(interval)
      clearTimeout(bail)
    }
  }, [inFlight])

  return (
    <MarketingLayout>
      <SeoHead title="Your SEO Audit" description={`SEO audit report for ${url}`} noindex />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Local SEO Audit</Eyebrow>

        {/* aria-live so a screen-reader user is told the audit finished, rather
            than having to notice the page quietly rewrote itself. */}
        <div role="status" aria-live="polite">
          {inFlight && !timedOut && <Running url={url} />}
          {inFlight && timedOut && <TimedOut url={url} />}
          {status === "failed" && <Failed url={url} />}
          {status === "complete" && results && (
            <Complete url={url} grade={grade!} score={score!} results={results} />
          )}
        </div>
      </Section>
    </MarketingLayout>
  )
}

function Running({ url }: { url: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">Auditing {url}…</h1>
      <p className="mt-5 text-lg text-ink-body">
        Fetching your site and running ten checks. This usually takes under
        thirty seconds — the page updates automatically.
      </p>
      <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-accent" aria-hidden="true" />
          <span className="font-medium text-ink-display">Running checks</span>
        </div>
        <ul className="mt-4 grid gap-2 text-sm text-ink-muted sm:grid-cols-2">
          {CHECK_NAMES.map((name) => (
            <li key={name}>· {name}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-ink-muted">
          Taking longer than expected? Leave this page open — or email{" "}
          <a href="mailto:hello@fullcast.co">hello@fullcast.co</a> and we'll send
          the report when it's done.
        </p>
      </div>
    </>
  )
}

// Polling gave up. The audit may well still finish — the job outlives this
// page — so this says what's actually true rather than declaring failure.
function TimedOut({ url }: { url: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">Still working on {url}</h1>
      <p className="mt-5 text-lg text-ink-body">
        This is taking longer than usual, so we've stopped refreshing the page.
        The audit is still running in the background and you'll get the report
        by email when it lands.
      </p>
      <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <p className="text-ink-body">
          You can reload this page to check again, or email{" "}
          <a href="mailto:hello@fullcast.co">hello@fullcast.co</a> if you don't
          hear from us — a site that's slow to respond is itself worth knowing
          about, and we'll look into it.
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => router.reload()}
            className="rounded-xl bg-ink-display px-5 py-2.5 text-page"
          >
            Check again
          </button>
        </div>
      </div>
    </>
  )
}

function Failed({ url }: { url: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">That one needs a human.</h1>
      <p className="mt-5 text-lg text-ink-body">
        Something about {url} tripped up the automated audit — which is
        sometimes itself a finding. Email us and we'll run it by hand, free.
      </p>
      <div className="mt-8">
        <Button asChild size="lg">
          <a href={`mailto:hello@fullcast.co?subject=Manual%20SEO%20audit%20request&body=Site:%20${encodeURIComponent(url)}`}>
            Request a manual audit
          </a>
        </Button>
      </div>
    </>
  )
}

function Complete({ url, grade, score, results }: { url: string; grade: string; score: number; results: Results }) {
  const fails = results.checks.filter((c) => c.status === "fail")
  const warns = results.checks.filter((c) => c.status === "warn")
  const passes = results.checks.filter((c) => c.status === "pass")

  return (
    <>
      <div className="mt-4 flex items-center gap-6">
        <GradeBadge grade={grade} size="lg" />
        <div>
          <h1 className="display-2">{url}</h1>
          <p className="mt-1 text-ink-muted">Scored {score}/100</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {fails.length > 0 && (
              <SummaryChip icon={XCircle} className="bg-danger-faded text-danger-display">
                {fails.length} {fails.length === 1 ? "failure" : "failures"}
              </SummaryChip>
            )}
            {warns.length > 0 && (
              <SummaryChip icon={AlertTriangle} className="bg-signal-faded text-signal-display">
                {warns.length} {warns.length === 1 ? "warning" : "warnings"}
              </SummaryChip>
            )}
            <SummaryChip icon={CheckCircle2} className="bg-success-faded text-success-display">
              {passes.length} passed
            </SummaryChip>
          </div>
        </div>
      </div>

      {fails.length > 0 && (
        <FindingGroup status="fail" title={`What's broken (${fails.length})`}>
          {fails.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}
      {warns.length > 0 && (
        <FindingGroup status="warn" title={`Worth fixing (${warns.length})`}>
          {warns.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}
      {passes.length > 0 && (
        <FindingGroup status="pass" title={`What's working (${passes.length})`}>
          {passes.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}

      <div className="mt-12 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <h2 className="display-2">Want these fixed?</h2>
        <p className="mt-3 text-ink-body">
          I run a local SEO service for businesses like yours — audits, fixes,
          and full builds. Tell me what you do and where you serve, and I'll
          reply with a straight read on what's worth doing first. No
          obligation either way — this report is yours to use with anyone.
        </p>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={`mailto:hello@fullcast.co?subject=SEO%20audit%20follow-up&body=My%20site:%20${encodeURIComponent(url)}%20(grade%20${grade})`}>
              Email me about this report
            </a>
          </Button>
        </div>
      </div>
    </>
  )
}

function SummaryChip({
  icon: Icon,
  className,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  className: string
  children: React.ReactNode
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${className}`}>
      <Icon className="h-4 w-4" />
      {children}
    </span>
  )
}
